import { useState, useCallback } from 'react';
import { cardViewApi } from '../services/api';
import { BankCard, OtpMedium } from '../types';
import { isLocalDevelopment } from '../utils/config';

interface UseCardViewRequestResult {
  // State
  phoneLastDigits: string | null;
  card: BankCard | null;
  loading: boolean;
  error: string | null;
  errorStatus: number | null;
  senderName: string | null;

  // Actions
  validate: (cvrt: string) => Promise<boolean>;
  requestOtp: (cvrt: string, phoneNumber: string, medium?: OtpMedium) => Promise<boolean>;
  verifyOtpAndGetCard: (cvrt: string, otp: string) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

export const useCardViewRequest = (): UseCardViewRequestResult => {
  const [phoneLastDigits, setPhoneLastDigits] = useState<string | null>(null);
  const [card, setCard] = useState<BankCard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [senderName, setSenderName] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
    setErrorStatus(null);
  }, []);

  const reset = useCallback(() => {
    setPhoneLastDigits(null);
    setCard(null);
    setLoading(false);
    setError(null);
    setErrorStatus(null);
    setSenderName(null);
  }, []);

  const validate = useCallback(async (cvrt: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const digits = await cardViewApi.validate(cvrt);
      setPhoneLastDigits(digits);
      return true;
    } catch (err: any) {
      const status = err.response?.status;
      const responseData = err.response?.data;
      
      // Log unexpected responses to console
      if (status && status !== 401 && status !== 404) {
        console.error(`Unexpected status ${status} from /validate:`, responseData);
      }
      
      let errorMessage = responseData?.moreInfo || responseData?.description || responseData?.message || 'Validation failed';
      
      // Handle specific status codes
      switch (status) {
        case 400:
          errorMessage = responseData?.moreInfo || responseData?.description || 'Invalid request. Please check your card view request token.';
          break;
        case 401:
        case 404:
          errorMessage = 'Card View Request does not exist or expired';
          break;
        case 500:
          errorMessage = responseData?.moreInfo || 'Server error occurred. Please try again later.';
          break;
        default:
          if (status >= 400) {
            errorMessage = `Request failed (Error ${status}): ${errorMessage}`;
          }
      }
      
      setError(errorMessage);
      setErrorStatus(status || null);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const requestOtp = useCallback(async (cvrt: string, phoneNumber: string, medium: OtpMedium = 'sms'): Promise<boolean> => {
    if (isLocalDevelopment()) {
      console.log('[requestOtp] Starting request, clearing previous error');
    }
    setError(null);
    setErrorStatus(null);
    setSenderName(null);
    setLoading(true);

    try {
      const response = await cardViewApi.requestOtp(cvrt, phoneNumber, medium);
      if (response.senderName) {
        setSenderName(response.senderName);
      }
      return true;
    } catch (err: any) {
      const status = err.response?.status;
      const responseData = err.response?.data;

      // Log unexpected responses to console
      if (status && status !== 401 && status !== 404 && status !== 400) {
        console.error(`Unexpected status ${status} from /requestotp:`, responseData);
      }

      // Debug logging
      if (isLocalDevelopment()) {
        console.log('Error response data:', responseData);
        console.log('Response data type:', typeof responseData);
      }

      let errorMessage = responseData?.moreInfo || responseData?.description || responseData?.message || 'Failed to send OTP';
      if (isLocalDevelopment()) {
        console.log('Extracted error message:', errorMessage);
      }
      
      // Handle specific status codes
      switch (status) {
        case 400:
          errorMessage = responseData?.moreInfo || responseData?.description || 'Phone number does not match the registered number';
          break;
        case 401:
        case 404:
          errorMessage = 'Card View Request does not exist or expired';
          break;
        case 500:
          errorMessage = responseData?.moreInfo || 'Server error occurred. Please try again later.';
          break;
        default:
          if (status >= 400) {
            errorMessage = `Request failed (Error ${status}): ${errorMessage}`;
          }
      }

      if (isLocalDevelopment()) {
        console.log('[requestOtp] Final error message:', errorMessage);
      }
      setError(errorMessage);
      setErrorStatus(status || null);
      if (isLocalDevelopment()) {
        console.log('[requestOtp] Error state set, returning false');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtpAndGetCard = useCallback(async (cvrt: string, otp: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const cardData = await cardViewApi.getCard(cvrt, otp);
      setCard(cardData);
      return true;
    } catch (err: any) {
      const status = err.response?.status;
      const responseData = err.response?.data;
      
      // Log unexpected responses to console
      if (status && status !== 401 && status !== 404 && status !== 400) {
        console.error(`Unexpected status ${status} from /card:`, responseData);
      }
      
      let errorMessage = responseData?.moreInfo || responseData?.description || responseData?.message || responseData || 'Failed to verify OTP or retrieve card';
      
      // Handle specific status codes
      switch (status) {
        case 400:
          errorMessage = responseData?.moreInfo || responseData?.description || 'Invalid verification code';
          break;
        case 401:
          // Check if it's specifically an OTP error
          if ((typeof responseData === 'string' && responseData.toLowerCase().includes('otp')) || 
              (responseData?.moreInfo && responseData.moreInfo.toLowerCase().includes('otp'))) {
            errorMessage = responseData?.moreInfo || 'Verification code is incorrect or expired';
          } else {
            errorMessage = 'Card View Request does not exist or expired';
          }
          break;
        case 404:
          errorMessage = 'Card View Request not found';
          break;
        case 500:
          errorMessage = responseData?.moreInfo || 'Server error occurred. Please try again later.';
          break;
        default:
          if (status >= 400) {
            errorMessage = `Request failed (Error ${status}): ${errorMessage}`;
          }
      }
      
      setError(errorMessage);
      setErrorStatus(status || null);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    phoneLastDigits,
    card,
    loading,
    error,
    errorStatus,
    senderName,
    validate,
    requestOtp,
    verifyOtpAndGetCard,
    clearError,
    reset,
  };
};